import React from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import FlairIcon, {
  FlairIconName,
  SizeProp,
} from '../../atomic/atoms/FlairIcon';
import { Placement } from 'react-bootstrap/types';
import {
  Button as UiButton,
  ButtonProps as UiButtonProps,
} from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export type ButtonProps = Omit<UiButtonProps, 'onClick'> & {
  label?: string;
  isProcessing?: boolean;
  icon?: FlairIconName;
  faIcon?: FlairIconName;
  className?: string;
  tooltip?: string;
  tooltipPlacement?: Placement;
  iconSize?: SizeProp;
  /**
   * Renders the button as an anchor. Kept for parity with the react-bootstrap
   * Button this wrapper replaced (see DX-884).
   */
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

const Icon: React.FC<
  React.PropsWithChildren<{
    isProcessing?: boolean;
    faIcon?: FlairIconName;
    icon?: FlairIconName;
    iconSize?: SizeProp;
  }>
> = ({ isProcessing, icon, faIcon, iconSize }) =>
  isProcessing ? (
    <Spinner as="span" size="sm" role="status" aria-hidden="true" />
  ) : icon ? (
    <FlairIcon icon={icon} className="align-middle" size={iconSize} />
  ) : faIcon ? (
    <FlairIcon icon={faIcon} size={iconSize} />
  ) : (
    <></>
  );

const Label: React.FC<
  React.PropsWithChildren<{
    label?: string;
    hasIcon: boolean;
    isProcessing?: boolean;
  }>
> = ({ label, hasIcon, isProcessing }) =>
  label ? (
    <span className={hasIcon || isProcessing ? 'ms-2' : ''}>{label}</span>
  ) : (
    <></>
  );

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      label,
      isProcessing,
      icon,
      faIcon,
      disabled,
      tooltip,
      iconSize,
      tooltipPlacement,
      href,
      children,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const hasLeadingVisual = !!(icon || faIcon || children);
    const content = (
      <>
        <Icon
          isProcessing={isProcessing}
          icon={icon}
          faIcon={faIcon}
          iconSize={iconSize}
        />
        <Label
          label={label}
          hasIcon={hasLeadingVisual}
          isProcessing={isProcessing}
        />
        {!isProcessing ? children : null}
      </>
    );

    const isDisabled = !!(isProcessing || disabled);
    /*
     * Dashkit gives a disabled link button the full palette via
     * `.btn.disabled` (background, border, colour and opacity). The shadcn
     * Button expresses those through `disabled:` utilities, which an anchor can
     * never match — so a disabled link would keep full colour and a pointer
     * cursor. Render a real disabled button instead: navigation is blocked
     * either way, and this inherits the correct disabled styling for free.
     */
    const button =
      href && !isDisabled ? (
        <UiButton asChild {...rest}>
          <a
            ref={(element) => setForwardedRef(ref, element)}
            href={href}
            onClick={(event) => onClick?.(event)}>
            {content}
          </a>
        </UiButton>
      ) : (
        <UiButton
          ref={(element) => setForwardedRef(ref, element)}
          disabled={isDisabled}
          onClick={(event) => onClick?.(event)}
          {...rest}>
          {content}
        </UiButton>
      );

    return tooltip ? (
      <OverlayTrigger
        overlay={<Tooltip id={`button-tooltip-${tooltip}`}>{tooltip}</Tooltip>}
        placement={tooltipPlacement}>
        <span>{button}</span>
      </OverlayTrigger>
    ) : (
      button
    );
  },
);
Button.displayName = 'Button';

function setForwardedRef(
  ref: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
  element: HTMLButtonElement | HTMLAnchorElement | null,
) {
  if (typeof ref === 'function') {
    ref(element);
  } else if (ref) {
    ref.current = element;
  }
}

export default Button;
